import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Fade } from "./Fade";

const benefits = [
  {
    number: "01",
    title: "Campaign-level capital allocation",
    description:
      "Dedicated capital deployed per campaign — precisely matched to your investment.",
  },
  {
    number: "02",
    title: "Performance-driven qualification",
    description:
      "Eligibility determined through real-time data signals with full manual override.",
  },
  {
    number: "03",
    title: "Execution-level capital deployment",
    description:
      "Capital is deployed dynamically at the point of execution — eliminating delays, transfers, and operational overhead.",
  },
];

export function CapitalSection() {
  return (
    <section className="px-5 sm:px-8 md:px-10 py-20 sm:py-28 border-t border-white/[0.06]">
      <div className="max-w-[1280px] mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          <div>
            <Fade>
              <p className="text-xs tracking-[0.2em] uppercase text-[hsl(217_91%_60%)] mb-4 font-medium">
                Embedded capital
              </p>
            </Fade>
            <Fade delay={0.05}>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-[-0.02em] mb-4">
                Unlock capital for your top-performing campaigns and scale results instantly.
              </h2>
            </Fade>
            <Fade delay={0.1}>
              <p className="text-white/40 text-base leading-relaxed mb-10">
                Move beyond budget funding constraints and approval cycles. Growth Lever embeds capital
                directly into campaign execution, dynamically matched to performance.
              </p>
            </Fade>

            <div className="space-y-6">
              {benefits.map((item, i) => (
                <Fade key={item.number} delay={0.1 + i * 0.06} y={10}>
                  <div className="flex gap-4">
                    <span className="text-xs text-white/15 font-mono shrink-0 pt-0.5">{item.number}</span>
                    <div>
                      <p className="text-sm font-semibold mb-1">{item.title}</p>
                      <p className="text-sm text-white/40 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </Fade>
              ))}
            </div>

            <Fade delay={0.3}>
              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-white font-semibold text-sm bg-[hsl(217_91%_60%)] px-7 py-3 rounded-full hover:bg-[hsl(217_91%_65%)] transition-colors"
                >
                  Get a Demo <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Fade>
          </div>

          {/* Visual - product screenshot */}
          <Fade delay={0.15} y={24} className="md:pt-12">
            <div className="border border-white/10 rounded-lg overflow-hidden bg-gradient-to-br from-[hsl(217_91%_60%)]/[0.04] to-transparent">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/[0.06] bg-white/[0.02]">
                <div className="h-2 w-2 rounded-full bg-white/10" />
                <div className="h-2 w-2 rounded-full bg-white/10" />
                <div className="h-2 w-2 rounded-full bg-white/10" />
                <span className="ml-3 text-[11px] text-white/20">Growth Lever — Co-investment</span>
              </div>
              <div className="relative aspect-[4/3] bg-[hsl(222_47%_8%)] flex items-center justify-center">
                {/* TODO: add real product screenshot at /screenshots/capital.png */}
                <img
                  src="/screenshots/capital.png"
                  alt="Growth Lever co-investment dashboard"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full text-white/15 text-sm">Product screenshot coming soon</div>';
                  }}
                />
              </div>
            </div>
          </Fade>
        </div>
      </div>
    </section>
  );
}
